import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  Video,
  Mic,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Loader2
} from 'lucide-react';

// Translations for the UI
const translations = {
  en: {
    pageTitle: 'Setup Your Equipment',
    pageSubtitle: "Let's test your camera and microphone to ensure the best interview experience",
    cameraMicAccess: 'Camera & Microphone Access',
    accessDescription: 'We need access to your camera and microphone to conduct the interview. Your browser will ask for permission when you click the button below.',
    camera: 'Camera',
    cameraDescription: 'For video recording',
    microphone: 'Microphone',
    microphoneDescription: 'For audio recording',
    requestingAccess: 'Requesting Access...',
    allowAccess: 'Allow Camera & Microphone Access',
    deviceSettings: 'Device Settings',
    videoPreview: 'Video Preview',
    microphoneTest: 'Microphone Test',
    micTestDescription: 'Speak normally to test your microphone. You should see the level indicator move.',
    audioLevel: 'Audio Level',
    noAudioDetected: 'No audio detected. Please check your microphone and speak closer to it.',
    setupTestResults: 'Setup Test Results',
    browser: 'Browser',
    device: 'Device',
    back: 'Back',
    saving: 'Saving...',
    everythingLooksGood: 'Everything Looks Good',
    accessDenied: 'Camera and microphone access denied. Please grant permissions and try again.',
    devicesNotFound: 'No camera or microphone found. Please connect devices and try again.',
    accessFailed: 'Failed to access camera and microphone. Please check your devices.',
    unexpectedError: 'An unexpected error occurred. Please try again.',
    switchDeviceError: 'Failed to switch device. Please try again.',
    saveResultsError: 'Failed to save setup results. You can continue anyway.'
  },
  es: {
    pageTitle: 'Configure su Equipo',
    pageSubtitle: 'Probemos su cámara y micrófono para asegurar la mejor experiencia de entrevista',
    cameraMicAccess: 'Acceso a Cámara y Micrófono',
    accessDescription: 'Necesitamos acceso a su cámara y micrófono para realizar la entrevista. Su navegador le pedirá permiso cuando haga clic en el botón de abajo.',
    camera: 'Cámara',
    cameraDescription: 'Para grabación de video',
    microphone: 'Micrófono',
    microphoneDescription: 'Para grabación de audio',
    requestingAccess: 'Solicitando Acceso...',
    allowAccess: 'Permitir Acceso a Cámara y Micrófono',
    deviceSettings: 'Configuración de Dispositivos',
    videoPreview: 'Vista Previa de Video',
    microphoneTest: 'Prueba de Micrófono',
    micTestDescription: 'Hable normalmente para probar su micrófono. Debería ver moverse el indicador de nivel.',
    audioLevel: 'Nivel de Audio',
    noAudioDetected: 'No se detecta audio. Por favor revise su micrófono y hable más cerca de él.',
    setupTestResults: 'Resultados de la Prueba',
    browser: 'Navegador',
    device: 'Dispositivo',
    back: 'Atrás',
    saving: 'Guardando...',
    everythingLooksGood: 'Todo Está Listo',
    accessDenied: 'Acceso a cámara y micrófono denegado. Por favor conceda los permisos e inténtelo de nuevo.',
    devicesNotFound: 'No se encontró cámara o micrófono. Por favor conecte los dispositivos e inténtelo de nuevo.',
    accessFailed: 'Error al acceder a la cámara y micrófono. Por favor revise sus dispositivos.',
    unexpectedError: 'Ocurrió un error inesperado. Por favor inténtelo de nuevo.',
    switchDeviceError: 'Error al cambiar dispositivo. Por favor inténtelo de nuevo.',
    saveResultsError: 'Error al guardar los resultados. Puede continuar de todos modos.'
  },
  ca: {
    pageTitle: 'Configureu el vostre Equip',
    pageSubtitle: 'Provem la vostra càmera i micròfon per assegurar la millor experiència d\'entrevista',
    cameraMicAccess: 'Accés a Càmera i Micròfon',
    accessDescription: 'Necessitem accés a la vostra càmera i micròfon per realitzar l\'entrevista. El vostre navegador us demanarà permís quan feu clic al botó de sota.',
    camera: 'Càmera',
    cameraDescription: 'Per a gravació de vídeo',
    microphone: 'Micròfon',
    microphoneDescription: 'Per a gravació d\'àudio',
    requestingAccess: 'Sol·licitant Accés...',
    allowAccess: 'Permetre Accés a Càmera i Micròfon',
    deviceSettings: 'Configuració de Dispositius',
    videoPreview: 'Vista Prèvia de Vídeo',
    microphoneTest: 'Prova de Micròfon',
    micTestDescription: 'Parleu normalment per provar el vostre micròfon. Hauríeu de veure moure\'s l\'indicador de nivell.',
    audioLevel: 'Nivell d\'Àudio',
    noAudioDetected: 'No es detecta àudio. Si us plau reviseu el vostre micròfon i parleu més a prop d\'ell.',
    setupTestResults: 'Resultats de la Prova',
    browser: 'Navegador',
    device: 'Dispositiu',
    back: 'Enrere',
    saving: 'Desant...',
    everythingLooksGood: 'Tot Està Llest',
    accessDenied: 'Accés a càmera i micròfon denegat. Si us plau concediu els permisos i torneu-ho a provar.',
    devicesNotFound: 'No s\'ha trobat càmera o micròfon. Si us plau connecteu els dispositius i torneu-ho a provar.',
    accessFailed: 'Error en accedir a la càmera i micròfon. Si us plau reviseu els vostres dispositius.',
    unexpectedError: 'S\'ha produït un error inesperat. Si us plau torneu-ho a provar.',
    switchDeviceError: 'Error en canviar dispositiu. Si us plau torneu-ho a provar.',
    saveResultsError: 'Error en desar els resultats. Podeu continuar de totes maneres.'
  }
};

type Language = keyof typeof translations;

interface ExpertSetupScreenProps {
  interview: {
    expertName: string;
    language?: string;
  };
  token: string;
  onNext: () => void;
  onBack: () => void;
  onDeviceSelected: (deviceId: string) => void;
}

export function ExpertSetupScreen({ interview, token, onNext, onBack, onDeviceSelected }: ExpertSetupScreenProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyzerRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [videoResolution, setVideoResolution] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<{
    audioOk: boolean;
    videoOk: boolean;
    browser: string;
    device: string;
  } | null>(null);
  const [isSavingResults, setIsSavingResults] = useState(false);

  // Device selection state
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('');
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');

  // Get translations for the interview language
  const lang = (interview.language || 'en') as Language;
  const t = translations[lang] || translations.en;

  // Define requestPermissions BEFORE useEffect that calls it (hoisting issue fix)
  const requestPermissions = async (audioDeviceId?: string, videoDeviceId?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const constraints: MediaStreamConstraints = {
        video: videoDeviceId
          ? { deviceId: { ideal: videoDeviceId }, width: { ideal: 1280 }, height: { ideal: 720 } }
          : { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: audioDeviceId
          ? {
              deviceId: { ideal: audioDeviceId },
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 24000,
              channelCount: 1
            }
          : {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 24000,
              channelCount: 1
            }
      };

      console.log('[Setup] Requesting media permissions with constraints:', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      streamRef.current = stream;
      console.log('[Setup] Got stream, videoRef.current is:', videoRef.current ? 'READY' : 'NULL');

      // Set video preview - retry if ref not ready
      const setVideoStream = () => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          console.log('[Setup] Video stream set, tracks:', stream.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled, readyState: t.readyState })));

          // Force video to play - critical for Safari and some browsers
          videoRef.current.onloadedmetadata = () => {
            console.log('[Setup] Video metadata loaded, attempting to play');
            videoRef.current?.play().catch(err => {
              console.error('[Setup] Error playing video:', err);
            });
          };
          return true;
        } else {
          console.warn('[Setup] Video ref not ready, will retry...');
          return false;
        }
      };

      // Try to set video stream immediately, retry if needed
      if (!setVideoStream()) {
        // Video ref not ready, wait a bit and retry
        setTimeout(() => {
          console.log('[Setup] Retrying video stream setup...');
          setVideoStream();
        }, 100);
      }

      // Get video resolution
      const videoTrack = stream.getVideoTracks()[0];
      const settings = videoTrack.getSettings();
      setVideoResolution(`${settings.width}x${settings.height}`);

      // Setup audio analyzer
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyzer = audioContext.createAnalyser();
      analyzerRef.current = analyzer;
      analyzer.fftSize = 256;
      source.connect(analyzer);

      // Start audio level monitoring
      monitorAudioLevel();

      setPermissionGranted(true);

      // Enumerate devices after permissions granted
      await enumerateDevices();

      // Automatically run tests after 2 seconds
      setTimeout(() => {
        runSetupTests();
      }, 2000);

    } catch (err) {
      console.error('[Setup] Error accessing media devices:', err);
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setError(t.accessDenied);
        } else if (err.name === 'NotFoundError') {
          setError(t.devicesNotFound);
        } else {
          setError(t.accessFailed);
        }
      } else {
        setError(t.unexpectedError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Auto-initialize camera and microphone on mount
    console.log('[Setup] Component mounted, auto-initializing media...');
    requestPermissions();

    return () => {
      // Cleanup on unmount
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const enumerateDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(d => d.kind === 'audioinput');
      const videoInputs = devices.filter(d => d.kind === 'videoinput');

      setAudioDevices(audioInputs);
      setVideoDevices(videoInputs);

      // Set default selected devices
      if (audioInputs.length > 0 && !selectedAudioDevice) {
        const defaultAudioDevice = audioInputs[0].deviceId;
        setSelectedAudioDevice(defaultAudioDevice);
        onDeviceSelected(defaultAudioDevice);
      }
      if (videoInputs.length > 0 && !selectedVideoDevice) {
        setSelectedVideoDevice(videoInputs[0].deviceId);
      }

      console.log('Available audio devices:', audioInputs);
      console.log('Available video devices:', videoInputs);
    } catch (err) {
      console.error('Error enumerating devices:', err);
    }
  };

  const switchDevice = async (type: 'audio' | 'video', deviceId: string) => {
    if (!streamRef.current) return;

    console.log(`[Setup] Switching ${type} device to:`, deviceId);

    try {
      // Update selected device state
      if (type === 'audio') {
        setSelectedAudioDevice(deviceId);
        console.log('[Setup] 🎤 Calling onDeviceSelected with deviceId:', deviceId);
        onDeviceSelected(deviceId);
      } else {
        setSelectedVideoDevice(deviceId);
      }

      // Stop all current tracks
      streamRef.current.getTracks().forEach(track => {
        console.log(`[Setup] Stopping track:`, track.kind, track.id);
        track.stop();
      });

      // Close audio context if exists
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      // Cancel animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      // Request new stream with selected devices
      const newAudioDeviceId = type === 'audio' ? deviceId : selectedAudioDevice;
      const newVideoDeviceId = type === 'video' ? deviceId : selectedVideoDevice;

      await requestPermissions(newAudioDeviceId, newVideoDeviceId);

      console.log(`[Setup] Successfully switched ${type} device`);
    } catch (err) {
      console.error(`[Setup] Error switching ${type} device:`, err);
      setError(t.switchDeviceError);
    }
  };

  const monitorAudioLevel = () => {
    if (!analyzerRef.current) return;

    const dataArray = new Uint8Array(analyzerRef.current.frequencyBinCount);

    const checkLevel = () => {
      if (!analyzerRef.current) return;

      analyzerRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      const normalized = Math.min(100, (average / 255) * 100 * 2); // Amplify for visibility
      setAudioLevel(normalized);

      animationFrameRef.current = requestAnimationFrame(checkLevel);
    };

    checkLevel();
  };

  const runSetupTests = () => {
    if (!streamRef.current) return;

    const audioTrack = streamRef.current.getAudioTracks()[0];
    const videoTrack = streamRef.current.getVideoTracks()[0];

    const results = {
      audioOk: audioTrack && audioTrack.readyState === 'live',
      videoOk: videoTrack && videoTrack.readyState === 'live',
      browser: navigator.userAgent.includes('Chrome') ? 'Chrome' :
               navigator.userAgent.includes('Firefox') ? 'Firefox' :
               navigator.userAgent.includes('Safari') ? 'Safari' : 'Unknown',
      device: navigator.platform || 'Unknown'
    };

    setTestResults(results);
  };

  const saveTestResults = async () => {
    if (!testResults) return;

    setIsSavingResults(true);

    try {
      const response = await fetch(`/api/expert/interview/test-setup?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audioLevel: audioLevel / 100,
          videoResolution,
          browser: testResults.browser,
          device: testResults.device
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save setup test results');
      }

      // Proceed to next screen
      onNext();
    } catch (err) {
      console.error('Error saving test results:', err);
      setError(t.saveResultsError);
      // Still allow proceeding
      setTimeout(() => onNext(), 2000);
    } finally {
      setIsSavingResults(false);
    }
  };

  const handleContinue = () => {
    if (testResults && testResults.audioOk && testResults.videoOk) {
      saveTestResults();
    }
  };

  return (
    <div className="min-h-screen p-4 py-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold mb-2">{t.pageTitle}</h1>
          <p className="text-muted-foreground">
            {t.pageSubtitle}
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Permission Request */}
        {!permissionGranted && (
          <Card>
            <CardHeader>
              <CardTitle>{t.cameraMicAccess}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t.accessDescription}
              </p>

              <div className="flex gap-4 p-4 bg-muted rounded-lg">
                <div className="flex-1 flex items-center gap-3">
                  <Video className="w-8 h-8 text-primary" />
                  <div>
                    <p className="font-medium">{t.camera}</p>
                    <p className="text-sm text-muted-foreground">{t.cameraDescription}</p>
                  </div>
                </div>
                <div className="flex-1 flex items-center gap-3">
                  <Mic className="w-8 h-8 text-primary" />
                  <div>
                    <p className="font-medium">{t.microphone}</p>
                    <p className="text-sm text-muted-foreground">{t.microphoneDescription}</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => requestPermissions()}
                disabled={isLoading}
                className="w-full"
                size="lg"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t.requestingAccess}
                  </>
                ) : (
                  <>
                    <Video className="w-4 h-4 mr-2" />
                    {t.allowAccess}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Setup Tests */}
        {permissionGranted && (
          <>
            {/* Device Selection */}
            {(audioDevices.length > 1 || videoDevices.length > 1) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">{t.deviceSettings}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {videoDevices.length > 1 && (
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        <Video className="w-4 h-4 inline mr-2" />
                        {t.camera}
                      </label>
                      <select
                        value={selectedVideoDevice}
                        onChange={(e) => switchDevice('video', e.target.value)}
                        className="w-full p-2 border rounded-lg bg-background"
                      >
                        {videoDevices.map((device) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Camera ${device.deviceId.substring(0, 8)}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {audioDevices.length > 1 && (
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        <Mic className="w-4 h-4 inline mr-2" />
                        {t.microphone}
                      </label>
                      <select
                        value={selectedAudioDevice}
                        onChange={(e) => switchDevice('audio', e.target.value)}
                        className="w-full p-2 border rounded-lg bg-background"
                      >
                        {audioDevices.map((device) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Microphone ${device.deviceId.substring(0, 8)}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Video Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  {t.videoPreview}
                  {testResults?.videoOk && (
                    <CheckCircle2 className="w-5 h-5 text-green-600 ml-auto" />
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                  {videoResolution && (
                    <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/70 text-white text-sm rounded-full">
                      {videoResolution}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Audio Level */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mic className="w-5 h-5" />
                  {t.microphoneTest}
                  {testResults?.audioOk && audioLevel > 5 && (
                    <CheckCircle2 className="w-5 h-5 text-green-600 ml-auto" />
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t.micTestDescription}
                </p>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t.audioLevel}</span>
                    <span className="font-mono">{Math.round(audioLevel)}%</span>
                  </div>
                  <Progress value={audioLevel} className="h-3" />
                </div>

                {/* Fixed height container to prevent layout shift */}
                <div className="min-h-[52px]">
                  {audioLevel < 5 && permissionGranted && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        {t.noAudioDetected}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Test Results */}
            {testResults && (
              <Card>
                <CardHeader>
                  <CardTitle>{t.setupTestResults}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-3">
                      <Video className="w-5 h-5" />
                      <span>{t.camera}</span>
                    </div>
                    {testResults.videoOk ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>

                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-3">
                      <Mic className="w-5 h-5" />
                      <span>{t.microphone}</span>
                    </div>
                    {testResults.audioOk ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>

                  <div className="pt-4 text-xs text-muted-foreground text-center">
                    {t.browser}: {testResults.browser} • {t.device}: {testResults.device}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={onBack}
            disabled={isSavingResults}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            {t.back}
          </Button>

          {testResults && testResults.audioOk && testResults.videoOk && (
            <Button
              onClick={handleContinue}
              disabled={isSavingResults}
              size="lg"
            >
              {isSavingResults ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t.saving}
                </>
              ) : (
                <>
                  {t.everythingLooksGood}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
